import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  role: 'aluno' | 'admin';
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchProfile = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      
      // If no profile exists, create one
      if (!data && user) {
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([{ 
            id: user.id, // Use user.id as the primary key
            user_id: user.id, 
            role: 'aluno' 
          }])
          .select()
          .single();
          
        if (createError) {
          console.error('Error creating profile:', createError);
        } else {
          setProfile(newProfile as Profile);
        }
      } else {
        setProfile(data as Profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      // Setar loading false imediatamente quando não há usuário
      setLoading(false);
      return;
    }

    fetchProfile();
  }, [user?.id, fetchProfile]); // Só re-executar se o ID do usuário mudar

  const isAdmin = profile?.role === 'admin';

  return {
    profile,
    loading,
    isAdmin,
    refetch: fetchProfile
  };
};